<template>
    <a-modal
        :visible="visible"
        :title="isEditing ? 'Kuralı Düzenle' : 'Yeni Kural Oluştur'"
        width="900px"
        :footer="null"
        @cancel="onCancel"
        :bodyStyle="{ padding: '24px' }"
    >
        <div class="rule-editor">
            <!-- Steps -->
            <a-steps :current="currentStep" size="small" class="steps-container" @change="onStepChange">
                <a-step title="Ne Zaman?" />
                <a-step title="Koşullar" />
                <a-step title="Aksiyonlar" />
            </a-steps>

            <!-- Step Content -->
            <div class="step-content">
                <!-- Step 1: WHEN -->
                <div v-if="currentStep === 0" class="when-step">
                    <h3>{{ $t("taskModuleRules.whenShouldRuleRun") }}</h3>
                    
                    <div class="rule-name-input">
                        <label>{{ $t("taskModuleRules.ruleName") }}</label>
                        <a-input
                            v-model="localRule.name"
                            placeholder="Kural için bir isim girin"
                            size="large"
                        />
                    </div>

                    <div class="when-options">
                        <div
                            v-for="option in whenOptions"
                            :key="option.value"
                            class="when-option"
                            :class="{ selected: localRule.when === option.value }"
                            @click="localRule.when = option.value"
                        >
                            <div class="option-icon">
                                <i class="material-icons">{{ option.icon }}</i>
                            </div>
                            <div class="option-content">
                                <h4>{{ option.label }}</h4>
                                <p>{{ option.description }}</p>
                            </div>
                            <div class="option-check">
                                <i v-if="localRule.when === option.value" class="material-icons">check_circle</i>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Step 2: IF (Conditions) -->
                <div v-if="currentStep === 1" class="if-step">
                    <h3>{{ $t("taskModuleRules.whichConditionsMustBeMet") }}</h3>
                    <FilterBuilder
                        v-model="localRule.conditions"
                        :when-type="localRule.when"
                        :workflow-id="workflowId"
                    />
                </div>

                <!-- Step 3: ACTIONS -->
                <div v-if="currentStep === 2" class="actions-step">
                    <h3>{{ $t("taskModuleRules.whichActionsShouldRun") }}</h3>
                    <ActionsEditor
                        v-model="localRule.actions"
                        :when-type="localRule.when"
                        :workflow-id="workflowId"
                    />

                    <!-- Advanced Settings -->
                    <div v-if="localRule" class="advanced-settings">
                        <h3>{{ $t("taskModuleRules.advancedSettings") }}</h3>
                        
                        <div class="setting-item">
                            <div class="setting-header">
                                <a-switch 
                                    v-model="localRule.triggerOtherRules" 
                                    @change="onTriggerOtherRulesChange"
                                />
                                <div class="setting-info">
                                    <h4>{{ $t("taskModuleRules.triggerOtherRules") }}</h4>
                                    <p>
                                        {{ $t("taskModuleRules.shouldOtherRulesRunWhenThisRuleUpdatesTask") }} 
                                        <a-tooltip>
                                            <template slot="title">
                                                Örneğin bu kural stage değiştirirse, "stage değiştiğinde" tetiklenen diğer kurallar da çalışır. 
                                                Döngü önlemi için maksimum 10 zincirleme kural çalışabilir.
                                            </template>
                                            <i class="material-icons info-icon">info</i>
                                        </a-tooltip>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="setting-item">
                            <div class="setting-header">
                                <a-switch 
                                    v-model="localRule.reportToAutomation" 
                                    @change="onReportToAutomationChange"
                                />
                                <div class="setting-info">
                                    <h4>Supsis Otomasyonlarına Bildir</h4>
                                    <p>
                                        Bu kuralın aksiyonları Supsis Otomasyon sistemine bildirilsin mi? 
                                        <a-tooltip>
                                            <template slot="title">
                                                Aktif edilirse, bu kuralın yaptığı değişiklikler (assignee değişimi, stage değişimi vb.) 
                                                Supsis Otomasyon sistemindeki ilgili otomasyonları tetikler.
                                            </template>
                                            <i class="material-icons info-icon">info</i>
                                        </a-tooltip>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="step-navigation">
                <a-button v-if="currentStep > 0" @click="currentStep--">
                    Geri
                </a-button>
                <div class="spacer"></div>
                <a-button @click="onCancel">
                    İptal
                </a-button>
                <a-button
                    v-if="currentStep < 2"
                    type="primary"
                    @click="currentStep++"
                    :disabled="!canProceed"
                >
                    İleri
                </a-button>
                <a-button
                    v-if="currentStep === 2"
                    type="primary"
                    @click="onSave"
                >
                    Kaydet
                </a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import FilterBuilder from "./components/FilterBuilder.vue";
import ActionsEditor from "./components/ActionsEditor.vue";
import log2 from "../../../../../utils/log2";

const log = log2("TaskRuleEditor");

export default {
    name: "TaskRuleEditor",
    components: {
        FilterBuilder,
        ActionsEditor
    },
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        rule: {
            type: Object,
            default: null
        },
        workflowId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            currentStep: 0,
            localRule: null,
            whenOptions: [
                {
                    value: 'task_created',
                    label: 'Görev Oluşturulduğunda',
                    description: 'Yeni bir görev oluşturulduğunda çalışır',
                    icon: 'add_task'
                },
                {
                    value: 'stage_changed',
                    label: 'Stage Değiştiğinde',
                    description: 'Görevin stage\'i değiştiğinde çalışır',
                    icon: 'arrow_forward'
                },
                {
                    value: 'completed_changed',
                    label: 'Completed Durumu Değiştiğinde',
                    description: 'Görevin completed durumu değiştiğinde çalışır',
                    icon: 'check_circle'
                },
                {
                    value: 'priority_changed',
                    label: 'Priority Değiştiğinde',
                    description: 'Görevin önceliği değiştiğinde çalışır',
                    icon: 'flag'
                },
                {
                    value: 'assignees_changed',
                    label: 'Assignees Değiştiğinde',
                    description: 'Görevin atananları değiştiğinde çalışır',
                    icon: 'person_add'
                },
                {
                    value: 'owner_assigned',
                    label: 'Owner Atandığında',
                    description: 'Göreve sahip atandığında çalışır',
                    icon: 'assignment_ind'
                },
                {
                    value: 'collaborators_changed',
                    label: 'Collaborators Değiştiğinde',
                    description: 'Görevin işbirlikçileri değiştiğinde çalışır',
                    icon: 'group_add'
                },
                {
                    value: 'followers_changed',
                    label: 'Followers Değiştiğinde',
                    description: 'Görevin takipçileri değiştiğinde çalışır',
                    icon: 'visibility'
                },
                {
                    value: 'done_changed',
                    label: 'Done Durumu Değiştiğinde',
                    description: 'Görev tamamlandı olarak işaretlendiğinde çalışır',
                    icon: 'done_all'
                },
                {
                    value: 'archived_changed',
                    label: 'Arşiv Durumu Değiştiğinde',
                    description: 'Görev arşivlendiğinde çalışır',
                    icon: 'archive'
                },
                {
                    value: 'tags_changed',
                    label: 'Etiketler Değiştiğinde',
                    description: 'Görevin etiketleri değiştiğinde çalışır',
                    icon: 'label'
                },
                {
                    value: 'checklist_updated',
                    label: 'Checklist İlerlediğinde',
                    description: 'Görevin checklist\'i güncellendiğinde çalışır',
                    icon: 'checklist'
                },
                {
                    value: 'checklist_completed',
                    label: 'Tüm Checklist Tamamlandığında',
                    description: 'Görevin tüm checklist öğeleri tamamlandığında çalışır',
                    icon: 'task_alt'
                },
                {
                    value: 'comment_added',
                    label: 'Yorum Eklendiğinde',
                    description: 'Göreve yorum eklendiğinde çalışır',
                    icon: 'comment'
                }
            ]
        };
    },
    computed: {
        isEditing() {
            return this.rule && this.rule.id;
        },
        canProceed() {
            if (this.currentStep === 0) {
                return this.localRule.name && this.localRule.name.trim().length > 0;
            }
            return true;
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.initializeLocalRule();
                this.currentStep = 0;
            }
        },
        rule: {
            deep: true,
            handler() {
                if (this.visible) {
                    this.initializeLocalRule();
                }
            }
        }
    },
    created() {
        this.initializeLocalRule();
    },
    methods: {
        initializeLocalRule() {
            if (this.rule) {
                const clonedRule = JSON.parse(JSON.stringify(this.rule));
                // Create a new object with all fields to ensure reactivity
                this.localRule = {
                    id: clonedRule.id,
                    name: clonedRule.name,
                    enabled: clonedRule.enabled,
                    order: clonedRule.order,
                    triggerOtherRules: clonedRule.triggerOtherRules !== undefined ? clonedRule.triggerOtherRules : false,
                    reportToAutomation: clonedRule.reportToAutomation !== undefined ? clonedRule.reportToAutomation : true,
                    when: clonedRule.when,
                    conditions: clonedRule.conditions,
                    actions: clonedRule.actions,
                    createdAt: clonedRule.createdAt,
                    updatedAt: clonedRule.updatedAt
                };
            } else {
                this.localRule = {
                    id: this.generateUUID(),
                    name: "Yeni Kural",
                    enabled: true,
                    order: 0,
                    triggerOtherRules: false,
                    reportToAutomation: true,
                    when: "task_created",
                    conditions: {
                        filterGroups: [],
                        groupOperator: "AND"
                    },
                    actions: [],
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
            }
        },

        onCancel() {
            this.$emit("close");
        },

        onSave() {
            this.localRule.updatedAt = new Date().toISOString();
            console.log("Saving rule with settings:", {
                triggerOtherRules: this.localRule.triggerOtherRules,
                reportToAutomation: this.localRule.reportToAutomation
            });
            this.$emit("save", this.localRule);
        },

        onStepChange(step) {
            this.currentStep = step;
        },

        onTriggerOtherRulesChange(value) {
            console.log("triggerOtherRules changed to:", value);
            this.$set(this.localRule, 'triggerOtherRules', value);
        },

        onReportToAutomationChange(value) {
            console.log("reportToAutomation changed to:", value);
            this.$set(this.localRule, 'reportToAutomation', value);
        },

        generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.rule-editor {
    .steps-container {
        margin-bottom: 32px;

        :deep(.ant-steps-item) {
            cursor: pointer;

            &:hover {
                .ant-steps-item-title {
                    color: #1890ff;
                }
            }
        }
    }

    .step-content {
        min-height: 500px;
        max-height: 60vh;
        overflow-y: auto;
        padding: 24px 0;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #1a1a1a;
            margin: 0 0 24px 0;
        }

        .when-step {
            .rule-name-input {
                margin-bottom: 32px;

                label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 8px;
                    color: #1a1a1a;
                }
            }

            .when-options {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 16px;

                .when-option {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 18px 20px;
                    border: 2px solid #e8e8e8;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                    background: #ffffff;
                    position: relative;
                    overflow: hidden;

                    &::before {
                        content: '';
                        position: absolute;
                        left: 0;
                        top: 0;
                        bottom: 0;
                        width: 0;
                        background: linear-gradient(135deg, #1890ff 0%, #0e6fd8 100%);
                        transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                        opacity: 0.05;
                    }

                    &:hover {
                        border-color: #1890ff;
                        background: #fafbff;
                        transform: translateY(-2px);
                        box-shadow: 0 6px 16px rgba(24, 144, 255, 0.12);

                        &::before {
                            width: 4px;
                            opacity: 1;
                        }

                        .option-icon {
                            transform: scale(1.1);

                            .material-icons {
                                color: #1890ff;
                            }
                        }
                    }

                    &.selected {
                        border-color: #1890ff;
                        background: linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 100%);
                        box-shadow: 0 8px 20px rgba(24, 144, 255, 0.2);
                        transform: translateY(-2px);

                        &::before {
                            width: 4px;
                            opacity: 1;
                        }

                        .option-icon {
                            background: linear-gradient(135deg, #1890ff 0%, #0e6fd8 100%);
                            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);

                            .material-icons {
                                color: #ffffff;
                            }
                        }

                        .option-content {
                            h4 {
                                color: #0e6fd8;
                            }
                        }

                        .option-check {
                            .material-icons {
                                color: #1890ff;
                            }
                        }
                    }

                    .option-icon {
                        width: 48px;
                        height: 48px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #f5f5f5;
                        border-radius: 10px;
                        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                        flex-shrink: 0;

                        .material-icons {
                            font-size: 28px;
                            color: #999;
                            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                        }
                    }

                    .option-content {
                        flex: 1;
                        min-width: 0;

                        h4 {
                            font-size: 14px;
                            font-weight: 600;
                            margin: 0 0 4px 0;
                            color: #1a1a1a;
                            line-height: 1.4;
                            transition: color 0.25s ease;
                        }

                        p {
                            font-size: 12px;
                            color: #666;
                            margin: 0;
                            line-height: 1.5;
                        }
                    }

                    .option-check {
                        flex-shrink: 0;
                        width: 24px;
                        height: 24px;
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        .material-icons {
                            font-size: 24px;
                            color: #e8e8e8;
                            transition: all 0.25s ease;
                        }
                    }
                }
            }
        }

        .advanced-settings {
            margin-top: 48px;
            padding-top: 32px;
            border-top: 2px dashed #e8e8e8;

            h3 {
                font-size: 16px;
                font-weight: 600;
                color: #1a1a1a;
                margin: 0 0 24px 0;
                display: flex;
                align-items: center;
                gap: 8px;

                &::before {
                    content: '';
                    display: inline-block;
                    width: 4px;
                    height: 16px;
                    background: #1890ff;
                    border-radius: 2px;
                }
            }

            .setting-item {
                margin-bottom: 24px;
                padding: 20px;
                background: #fafafa;
                border-radius: 8px;
                border: 1px solid #e8e8e8;

                &:last-child {
                    margin-bottom: 0;
                }

                .setting-header {
                    display: flex;
                    gap: 16px;
                    align-items: flex-start;

                    .setting-info {
                        flex: 1;

                        h4 {
                            font-size: 14px;
                            font-weight: 600;
                            color: #1a1a1a;
                            margin: 0 0 6px 0;
                        }

                        p {
                            font-size: 13px;
                            color: #666;
                            margin: 0;
                            line-height: 1.6;
                            display: flex;
                            align-items: center;
                            gap: 4px;

                            .info-icon {
                                font-size: 16px;
                                color: #999;
                                cursor: help;
                                
                                &:hover {
                                    color: #1890ff;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    .step-navigation {
        display: flex;
        gap: 12px;
        padding-top: 24px;
        border-top: 1px solid #f0f0f0;

        .spacer {
            flex: 1;
        }
    }
}
</style>
